import { useState, useCallback, useMemo } from 'react';

export interface UsePaginationOptions<T> {
  /** The list of items to paginate */
  items: T[];
  /** Number of items per page (default: 10) */
  pageSize?: number;
  /** Initial page number (default: 1) */
  initialPage?: number;
}

export interface UsePaginationReturn<T> {
  /** Current page number (1-based) */
  currentPage: number;
  totalPages: number;
  currentPageItems: T[];
  totalItems: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  nextPage: () => void;
  previousPage: () => void;
  goToPage: (page: number) => void;
  goToFirstPage: () => void;
  goToLastPage: () => void;
  setPage: (page: number) => void;
  setItems: (items: T[]) => void;
}

/**
 * Headless pagination hook that manages pagination state and logic.
 * Provides all necessary functions and data for implementing custom pagination UI.
 *
 * @example
 * ```tsx
 * const {
 *   currentPage,
 *   totalPages,
 *   currentPageItems,
 *   nextPage,
 *   previousPage,
 *   goToPage,
 *   hasNextPage,
 *   hasPreviousPage,
 *   setItems,
 * } = usePagination({
 *   items: myItems,
 *   pageSize: 10,
 * });
 * ```
 */
export const usePagination = <T>({
  items: initialItems,
  pageSize = 10,
  initialPage = 1,
}: UsePaginationOptions<T>): UsePaginationReturn<T> => {
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [items, setItems] = useState<T[]>(initialItems);

  // Calculate total pages based on items and page size
  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(items.length / pageSize));
  }, [items.length, pageSize]);

  // Get items for the current page
  const currentPageItems = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    return items.slice(startIndex, endIndex);
  }, [items, currentPage, pageSize]);

  // Check if there's a next page
  const hasNextPage = currentPage < totalPages;

  // Check if there's a previous page
  const hasPreviousPage = currentPage > 1;

  // Navigate to the next page
  const nextPage = useCallback(() => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  }, [totalPages]);

  // Navigate to the previous page
  const previousPage = useCallback(() => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  }, []);

  // Navigate to a specific page with boundary validation
  const goToPage = useCallback(
    (page: number) => {
      const validatedPage = Math.max(1, Math.min(page, totalPages));
      setCurrentPage(validatedPage);
    },
    [totalPages],
  );

  // Navigate to the first page
  const goToFirstPage = useCallback(() => {
    setCurrentPage(1);
  }, []);

  // Navigate to the last page
  const goToLastPage = useCallback(() => {
    setCurrentPage(totalPages);
  }, [totalPages]);

  // Set the current page directly (alias for goToPage)
  const setPage = goToPage;

  return {
    currentPage,
    totalPages,
    currentPageItems,
    totalItems: items.length,
    hasNextPage,
    hasPreviousPage,
    nextPage,
    previousPage,
    goToPage,
    goToFirstPage,
    goToLastPage,
    setPage,
    setItems,
  };
};
